import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { t_dataName } from 'src/app/services/apiHandler/basicMachineFunctions/basic-machine-functions.service';
import { DidConfigService } from 'src/app/services/apiHandler/did-config/did-config.service';
import { DidDataRetrievalService, t_DataIdentifier } from 'src/app/services/apiHandler/did-data-retrieval/did-data-retrieval.service';
import { GatewayConnectionService } from 'src/app/services/apiHandler/gateway-connection/gateway-connection.service';
import { ConfDialogComponent } from '../confirmation-dialog/conf-dialog/conf-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { GlobalService } from 'src/app/services/helper/global-helper';


export type t_tableData = {
  name: string,
  ecu?: number,
  did?: number,
  info?: string,
  unit?: string,
  valid?: boolean,
  defaultValue: number,
  currentValue?: number,
  chosenValue?: any
  valueOptions: {
    inputType: string,
    inputValue?: number[],
    range?: number[]
  }
}


@Component({
  selector: 'app-parameter-data',
  templateUrl: './parameter-data.component.html',
  styleUrls: ['./parameter-data.component.scss']
})
export class ParameterDataComponent implements OnInit,OnDestroy {

  private dataNames: t_dataName[] = [];
  public displayColumns: string[] = ["name", "default_value", "curr_value", "input_value" , "btn_set", "btn_default"];
  public dataSet: MatTableDataSource<t_tableData> = new MatTableDataSource<t_tableData>;
  @ViewChild(MatTable) table: MatTable<any> | undefined;
  public loaded: boolean = false;
  public selectControl = new FormControl("", Validators.required);
  private snackbar = inject(MatSnackBar);
  public writablePara: t_tableData[] = [];
  public fetchedElements: string[] = [];
  private line: string = '';
  

  constructor(private didService: DidConfigService, public gatewayConnectionService: GatewayConnectionService, private retrievalService: DidDataRetrievalService, public dialog: MatDialog, public translate: TranslateService, private global: GlobalService){
    this.global.machineInfos.subscribe({
      next: newValue => {this.line = newValue.line;}
    });
  }

  ngOnInit(): void {
    this.setupServices().then(() => {
      this.retrievalService.initService();
      this.retrievalService.startDataQuery();
      this.writablePara.forEach((para) => {
        if(this.dataNames.some((e) => e.name === para.name)){
          let identifier = this.dataNames[this.dataNames.findIndex((e) => e.name === para.name)].identifier;
          let info = "ecu: " + identifier?.ecu_id + ", id: " + identifier?.data_id; 
          para.ecu = identifier?.ecu_id;
          para.did = identifier?.data_id;
          para.info = info;
          para.valid = false;
          //get current value
          this.retrievalService.getDidsDetails([identifier!]).subscribe((res) => {
            let response = JSON.parse(JSON.stringify(res.body));
            let data;
            if(response[0].data.length > 0) {
              data = response[0].data[response[0].data.length - 1].value[0];
            } else {
              data = undefined;
            }
            para.currentValue = data;
          });
          this.dataSet.data.push(para);  
          this.fetchedElements.push(para.name);        
        }
      });
      this.loaded = true;
    });
  }

  ngOnDestroy(): void {
    this.retrievalService.stopSendQueryAliveState();
    this.retrievalService.stopDataQuery();
  }

  private setupServices(): Promise<void> {
    return new Promise((resolve) => {
      this.didService.getDidQueryList().subscribe((res) => {
        let dataIdentifiers: t_DataIdentifier[] = JSON.parse(JSON.stringify(res.body));
        dataIdentifiers.forEach((element: t_DataIdentifier) => {
          let info = this.didService.getDidInformation(element);
          let name = info?.did_name;
          this.dataNames.push({name: name!, identifier: element, didInformation: info}); 
        });
        // in future: fetch from backend
        fetch('../../../assets/WritableParameters_' + this.line + 'line.json').then( res => res.json()).then (json => {
          this.writablePara = json;
          resolve();
        });
      });
    });
  }

  public changeValue(element: t_tableData){
    this.handleDialog([element]);
  }

  public setDefaultValue(element: t_tableData){
    this.didService.writeDataByID(element.ecu!, element.did!, Number(element.defaultValue)).subscribe();
    this.updateDataset(element);
  }

  private handleDialog(data: t_tableData[]) {
    this.dialog.open(ConfDialogComponent, {
      data: data,
    }).afterClosed().subscribe((res) => {
      if(res != "") {
        res.forEach((set: t_tableData) => {
          this.updateDataset(set);
        })
        let message = this.translate.instant('MESSAGES.PAR_CHANGE', {value: res.length});
        this.snackbar.open(message, '', {duration: 3000});
      }
    });
  }

  public addAll(){
    let validSet: t_tableData[] = [];
    this.dataSet.data.forEach((set) => {
      if(set.chosenValue != undefined) {
        if(set.valueOptions.inputType == 'range'){
          let min = set.valueOptions.range![0];
          let max = set.valueOptions.range![1];
          if(set.chosenValue >= min && set.chosenValue <= max ){
            validSet.push(set);
          }
        } else {
          validSet.push(set);
        }      
      }
    });
    this.handleDialog(validSet); 
  }

  public resetAll(){
    this.dataSet.data.forEach((set) => {
      this.didService.writeDataByID(set.ecu!, set.did!, Number(set.defaultValue)).subscribe();
      this.updateDataset(set);
    });
  }

  public updateDataset(element: t_tableData) {
    this.fetchedElements = this.fetchedElements.filter(item => !item.includes(element.name));
    if(this.dataSet.data.includes(element)){
      let index = this.dataSet.data.findIndex(data => data.name === element.name);     
      let identifier: t_DataIdentifier = {ecu_id: element.ecu!, data_id: element.did!};
      // half second timeout to get new data from backend
      setTimeout(()=> {
        this.retrievalService.getDidsDetails([identifier]).subscribe((res) => {
          this.dataSet.data[index].chosenValue = undefined;
          this.dataSet.data[index].valid = false;
          let response = JSON.parse(JSON.stringify(res.body));
          if(response[0].data.length > 0) {
            if(response[0].data[response[0].data.length - 1].value != undefined) {
              let newData = response[0].data[response[0].data.length - 1].value[0];
              this.dataSet.data[index].currentValue = newData;
              this.table?.renderRows();
              this.fetchedElements.push(this.dataSet.data[index].name);
            } else {
              this.updateDataset(element); // should be no problem anymore but let it in for fallback 
            }
          }
        });
      }, 500);
    }
  }

  public setValidation(element: t_tableData){
    if(element.chosenValue != null) {
      element.valid = true;
    } else {
      element.valid = false;
    }
  }

  public checkRange(element: t_tableData, value: number, min: string, max: string){
    if(Number(min) <= Number(value) && Number(value) <= Number(max)){
      element.valid = true;
    } else {
      element.valid = false;
    }
    if(value == null) {
      element.valid = false;
    }  
  }

}